(() => {
  const selectors = {
    dialog: '[data-vo-newsletter-dialog]',
    form: '.vo-newsletter-overlay__form',
    field: '[data-vo-newsletter-field]',
    input: '[data-vo-newsletter-input]',
    error: '[data-vo-newsletter-error]',
    success: '[data-vo-newsletter-success]',
    defaultContent: '[data-vo-newsletter-default]',
    close: '[data-vo-newsletter-close]',
    initialFocus: '[data-vo-newsletter-initial-focus]',
    autoOpen: '[data-vo-newsletter-auto-open]',
    opener:
      '[data-vo-footer-newsletter-modal-trigger="newsletter-dialog"], [data-modal-id="newsletter-dialog"]',
  };

  const storageKeys = {
    lastSubmit: 'vo-newsletter-last-submit-dialog',
    successSource: 'vo-newsletter-success-source',
  };

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

  const focusInitialElement = (dialog) => {
    const initialFocusTarget =
      dialog.querySelector(selectors.success) ||
      dialog.querySelector(selectors.initialFocus);
    initialFocusTarget?.focus({ preventScroll: true });
  };

  const openDialog = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    dialog.showModal();
    focusInitialElement(dialog);
  };

  const bindForm = (dialog) => {
    const form = dialog.querySelector(selectors.form);
    const field = dialog.querySelector(selectors.field);
    const input = dialog.querySelector(selectors.input);
    const error = dialog.querySelector(selectors.error);

    if (!form || !field || !input || !error) return;
    if (form.dataset.voNewsletterOverlayBound === 'true') return;

    form.dataset.voNewsletterOverlayBound = 'true';

    const toggleFilledState = () => {
      field.classList.toggle('vo-newsletter-overlay__field--filled', input.value.trim() !== '');
    };

    const clearError = () => {
      field.classList.remove('vo-newsletter-overlay__field--error');
      error.textContent = '';
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    };

    const showError = (message) => {
      field.classList.add('vo-newsletter-overlay__field--error');
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');

      if (error.id) {
        input.setAttribute('aria-describedby', error.id);
      }
    };

    const validate = () => {
      const value = input.value.trim();

      if (value === '') {
        showError(input.dataset.emptyError || 'This field cannot be empty');
        return false;
      }

      if (!emailPattern.test(value)) {
        showError(input.dataset.invalidError || 'Please enter a valid e-mail address');
        return false;
      }

      input.value = value;
      clearError();
      return true;
    };

    toggleFilledState();

    input.addEventListener('input', () => {
      toggleFilledState();

      if (!error.hidden) {
        clearError();
      }
    });

    form.addEventListener('submit', (event) => {
      toggleFilledState();

      if (!validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.focus();
        return;
      }

      try {
        sessionStorage.setItem(storageKeys.lastSubmit, dialog.id);

        if (dialog.dataset.voNewsletterSuccessDestination !== 'overlay') {
          sessionStorage.removeItem(storageKeys.successSource);
          return;
        }

        sessionStorage.setItem(storageKeys.successSource, 'overlay');
      } catch (error) {
        // no-op: sessionStorage can be unavailable in privacy-restricted contexts
      }
    });
  };

  const resetServerState = (dialog) => {
    const field = dialog.querySelector(selectors.field);
    const input = dialog.querySelector(selectors.input);
    const error = dialog.querySelector(selectors.error);
    const success = dialog.querySelector(selectors.success);
    const defaultContent = dialog.querySelector(selectors.defaultContent);

    dialog.querySelector(selectors.autoOpen)?.remove();
    success?.remove();
    dialog.classList.remove('vo-newsletter-overlay--success');
    defaultContent?.removeAttribute('aria-hidden');

    if (error) {
      error.textContent = '';
      error.hidden = true;
    }

    if (input) {
      input.value = input.dataset.initialValue || '';
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }

    if (field) {
      field.classList.remove('vo-newsletter-overlay__field--error');
      field.classList.toggle('vo-newsletter-overlay__field--filled', (input?.value || '').trim() !== '');
    }
  };

  const bindDialog = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.dataset.voNewsletterDialogBound === 'true') return;

    dialog.dataset.voNewsletterDialogBound = 'true';

    bindForm(dialog);

    dialog.querySelector(selectors.close)?.addEventListener('click', () => {
      dialog.close();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close();
      }
    });

    dialog.addEventListener('close', () => {
      const opener = dialog.__voOpenedBy;

      if (opener instanceof HTMLElement) {
        opener.focus({ preventScroll: true });
      }
    });
  };

  const init = () => {
    const dialog = document.querySelector(selectors.dialog);
    if (!(dialog instanceof HTMLDialogElement)) return;

    bindDialog(dialog);

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const opener = event.target.closest(selectors.opener);
        if (!opener) return;

        dialog.__voOpenedBy = opener;

        window.setTimeout(() => {
          if (dialog.open) {
            focusInitialElement(dialog);
          }
        }, 0);
      },
      true
    );

    let lastSubmitDialog = '';

    try {
      lastSubmitDialog = sessionStorage.getItem(storageKeys.lastSubmit) || '';
    } catch (error) {
      lastSubmitDialog = '';
    }

    const autoOpenInput = dialog.querySelector(selectors.autoOpen);
    const autoOpenReason = autoOpenInput?.value || '';
    const params = new URLSearchParams(window.location.search);
    const hasOverlaySubmitMarker =
      params.get('newsletter_overlay_submitted') === 'true' || params.get('newsletter_subscribed') === 'true';

    if (!autoOpenInput) return;

    if (lastSubmitDialog === dialog.id || (autoOpenReason === 'success' && hasOverlaySubmitMarker)) {
      if (dialog.querySelector(selectors.success)) {
        dialog.classList.add('vo-newsletter-overlay--success');
        dialog.querySelector(selectors.defaultContent)?.setAttribute('aria-hidden', 'true');
      }

      openDialog(dialog);

      try {
        sessionStorage.removeItem(storageKeys.lastSubmit);
      } catch (error) {
        // no-op: sessionStorage can be unavailable in privacy-restricted contexts
      }

      return;
    }

    resetServerState(dialog);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
